import React, {useState} from 'react';
import styled from 'styled-components';
import type {MessageActivity} from '../types/activityEvent';
import {sanitizeMessageHtml} from '../utils/sanitizeMessageHtml';
import {rewriteInlineImageSrcs} from '../utils/rewriteInlineImageSrcs';
import {splitQuotedContent} from '../utils/splitQuotedContent';
import {DeliveryStatusBadge} from './DeliveryStatusBadge';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

interface Props {
	event: MessageActivity;
	onRetry?: (messageId: string) => void;
}

const Content = styled.div`
	padding: 16px 20px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #1e293b;
`;

const BodyText = styled.p`
	margin: 0;
	white-space: pre-wrap;
`;

const AttachmentList = styled.ul`
	margin: 12px 0 0;
	padding: 12px 0 0;
	list-style: none;
	border-top: 1px solid #f1f5f9;
	> li + li {
		margin-top: 4px;
	}
`;

const AttachmentItem = styled.li`
	display: flex;
	align-items: center;
	gap: 6px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #475569;
`;

const AttachmentLink = styled.a`
	font-weight: 500;
	color: #4f47d6;
	text-decoration: underline;
	&:hover {
		color: #3d36b3;
	}
`;

const StatusFooter = styled.div`
	margin-top: 8px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const LinkButton = styled.button<{$muted?: boolean}>`
	border: 0;
	background: transparent;
	padding: 0;
	cursor: pointer;
	font: ${p => p.theme.ticketing.font.body3};
	text-decoration: underline;
	color: ${p => (p.$muted ? '#94a3b8' : '#4f47d6')};
	&:hover {
		color: ${p => (p.$muted ? '#475569' : '#3d36b3')};
	}
`;

const ToggleButton = styled(LinkButton)`
	margin-top: 8px;
`;

const QuotedContent = styled.div`
	margin-top: 8px;
	border-top: 1px solid #f1f5f9;
	padding-top: 8px;
	color: #64748b;
`;

export function MessageBubble({event, onRetry}: Props) {
	const [quotedVisible, setQuotedVisible] = useState(false);

	const regularAttachments = event.attachments.filter(a => a.inline_content_id == null);

	const isOutbound = event.direction === 'outbound' || event.author_type === 'agent';

	const showStatusBadge = isOutbound && event.delivery_status !== null && event.delivery_status !== 'sent';

	const showRetryButton = isOutbound && event.delivery_status === 'failed' && event.retry_count < 3 && onRetry != null;

	function renderAttachments() {
		if (regularAttachments.length === 0) return null;
		return (
			<AttachmentList>
				{regularAttachments.map(a => (
					<AttachmentItem key={a.id}>
						<span aria-hidden="true">📎</span>
						<AttachmentLink
							href={ticketingAttachmentDownloadUrl(a.downloadUrl)}
							target="_blank"
							rel="noopener noreferrer"
						>
							{a.filename}
						</AttachmentLink>
					</AttachmentItem>
				))}
			</AttachmentList>
		);
	}

	function renderStatusFooter() {
		if (!showStatusBadge && !showRetryButton) return null;
		return (
			<StatusFooter>
				{showStatusBadge && (
					<DeliveryStatusBadge delivery_status={event.delivery_status} retry_count={event.retry_count} />
				)}
				{showRetryButton && (
					<LinkButton type="button" onClick={() => onRetry(event.id)}>
						Retry
					</LinkButton>
				)}
			</StatusFooter>
		);
	}

	if (event.body_html != null) {
		const sanitized = rewriteInlineImageSrcs(sanitizeMessageHtml(event.body_html), ticketingAttachmentDownloadUrl);
		const {main, quoted} = splitQuotedContent(sanitized);
		const hasQuoted = quoted.trim() !== '';

		return (
			<Content data-testid="message-content">
				<div dangerouslySetInnerHTML={{__html: main}} />
				{hasQuoted && (
					<>
						<ToggleButton type="button" $muted onClick={() => setQuotedVisible(v => !v)}>
							{quotedVisible ? 'Hide quoted content' : 'Show quoted content'}
						</ToggleButton>
						{quotedVisible && <QuotedContent dangerouslySetInnerHTML={{__html: quoted}} />}
					</>
				)}
				{renderAttachments()}
				{renderStatusFooter()}
			</Content>
		);
	}

	return (
		<Content data-testid="message-content">
			<BodyText>{event.body}</BodyText>
			{renderAttachments()}
			{renderStatusFooter()}
		</Content>
	);
}
